import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useIsMobile } from "../hooks/useIsMobile";
import MagazineTopSection from "../template/magazine/MagazineTopSection";
import { dbService } from "../firebaseInstance";
// import MultiCarousel from "../components/carousel/MultiCarousel";
// import ParticipantsMarquee from "../components/marquee/ParticipantsMarquee";
import MagazineParticipantsStrip from "../template/magazine/MagazineParticipantsStrip";
import MagazineCoverCarousel from "../template/magazine/MagazineCoverCarousel";
import MagazineSectionsBlock from "../template/magazine/MagazineSectionsBlock";
import ThreadsCafeMap from "./ThreadsCafeMap";
import SectionTitle from "../template/magazine/SectionTitle";
import { useTranslation } from "react-i18next";
import MagazineIssueHeader from "../template/magazine/MagazineIssueHeader";
import MagazineSectionContainer from "../template/magazine/MagazineSectionContainer";
import LineGrid from "../template/magazine/LineGrid";
import MagazineCollectionCard from "../template/magazine/MagazineCollectionCard";
import MagazineNextIssueCard from "../template/magazine/MagazineNextIssueCard";
import MagazineFooterBanner from "../template/magazine/MagazineFooterBanner";
import MagazineFooterBannerCard from "../template/magazine/MagazineFooterBannerCard";
import MagazineLoginPrompt from "../template/magazine/MagazineLoginPrompt";
import CollectionPopupModal from "../template/magazine/CollectionPopupModal";
import ParticipantsShowcaseSection from "../template/magazine/ParticipantsShowcaseSection";
import { useUser } from "../contexts/UserContext";
import { useVideoCache, getCacheInfo } from "../hooks/useVideoCache";
import { useMultipleVideoCache } from "../hooks/useMultipleVideoCache";

const MAG_ROOT = "magazines";
const MAG_ID = "monthlyThreads";

const MagazineIssue = () => {
	const { issueId } = useParams();
	const location = useLocation();
	const isMobile = useIsMobile("xl");
	useTranslation();
	const { isLogin } = useUser();

	const [issue, setIssue] = useState(null);
	const [items, setItems] = useState([]);
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);
	const [issueError, setIssueError] = useState(null); // kept for future UI; suppress unused by referencing below
	const [itemsError, setItemsError] = useState(null);
	const [activeRefType, setActiveRefType] = useState(null); // 'issues' | 'issue'
	// const [issueLoaded, setIssueLoaded] = useState(false); // reserved for future loading state UI
	const [collectionInfoById, setCollectionInfoById] = useState({});
	const [userInfoById, setUserInfoById] = useState({});
	const [threadInfoById, setThreadInfoById] = useState({});
	const [selectedCollectionId, setSelectedCollectionId] = useState(null);
	const [showCafeRegistrationModal, setShowCafeRegistrationModal] =
		useState(false);
	const [shuffledCoverStoryThreads, setShuffledCoverStoryThreads] = useState(
		[]
	);
	const [participants, setParticipants] = useState([]);
	const [shuffledSections, setShuffledSections] = useState([]);
	const participantsIdsRef = useRef([]);
	const [participantsReady, setParticipantsReady] = useState(false);

	// 로딩 상태
	const [issueLoading, setIssueLoading] = useState(true);
	const [collectionsLoading, setCollectionsLoading] = useState(true);
	const [usersLoading, setUsersLoading] = useState(true);

	// 현재 이슈에 로드된 모든 컬렉션 목록 (Admin 모달용)
	const collectionsForIssue = useMemo(() => {
		const ids = Array.from(
			new Set(items.map((it) => it.collectionId).filter(Boolean))
		);
		return ids
			.map((id) => collectionInfoById[id])
			.filter(Boolean)
			.map((col) => ({ id: col.id, title: col.title || col.name || col.id }));
	}, [items, collectionInfoById]);

	// 메인 커버 비디오 URL 생성
	const coverVideoUrl = useMemo(() => {
		if (!issueId) return null;
		return `https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fmonthly-cover-${issueId}.mp4?alt=media`;
	}, [issueId]);

	// 이슈 변경 시 참가자 순서/준비 상태 초기화
	useEffect(() => {
		participantsIdsRef.current = [];
		setParticipantsReady(false);
		setParticipants([]);
	}, [issueId]);

	// 섹션 비디오 URLs 생성
	const sectionVideoUrls = useMemo(() => {
		if (!issue?.sectionList || !issueId) return [];

		return issue.sectionList.map((_, index) => {
			const sectionNumber = index + 1;
			return `https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fsections%2F${issueId}-s${sectionNumber}.mp4?alt=media`;
		});
	}, [issue, issueId]);

	// 커버 비디오 캐싱 (높은 우선순위)
	const coverVideoCache = useVideoCache(coverVideoUrl, {
		preload: true,
		priority: 10,
	});

	// 섹션 비디오들 캐싱 (동적 개수 지원)
	const sectionVideoCaches = useMultipleVideoCache(sectionVideoUrls, {
		preload: false, // 지연 캐싱 (Intersection Observer로 제어)
		basePriority: 7, // 첫 번째 섹션이 가장 높은 우선순위
	});

	// 첫 번째 섹션 비디오는 최초 1회만 사전 캐싱
	const firstSectionPrecachedRef = useRef(false);
	useEffect(() => {
		if (firstSectionPrecachedRef.current) return;
		const firstSectionCache = sectionVideoCaches[0];
		if (
			firstSectionCache &&
			!firstSectionCache.isCached &&
			!firstSectionCache.isLoading
		) {
			if (process.env.NODE_ENV === "development") {
				console.log("🚀 Pre-caching first section video");
			}
			setTimeout(() => {
				try {
					firstSectionCache.cacheVideo();
				} catch (_) { }
				firstSectionPrecachedRef.current = true;
			}, 1000);
		}
	}, [sectionVideoCaches.length]);

	// 스크롤 위치 저장 및 복원 (History API 사용)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const restoreScrollFromHistory = () => {
			// History state에서 스크롤 위치 복원
			const state = window.history.state;
			if (state?.scrollY && typeof state.scrollY === "number") {
				const attemptRestore = (delay = 0) => {
					setTimeout(() => {
						window.scrollTo(0, state.scrollY);
						console.log("Restored scroll to:", state.scrollY);
					}, delay);
				};

				// 여러 시점에 복원 시도
				attemptRestore(0);
				attemptRestore(100);
				attemptRestore(300);
				attemptRestore(500);
			}
		};

		// 컴포넌트 마운트 시 스크롤 복원
		restoreScrollFromHistory();

		// 스크롤 위치를 History state에 저장
		let scrollTimeout;
		const saveScrollToHistory = () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				const currentState = window.history.state || {};
				const newState = { ...currentState, scrollY: window.scrollY };
				window.history.replaceState(
					newState,
					"",
					location.pathname + location.search
				);
			}, 100);
		};

		// Popstate 이벤트 리스너 (뒤로가기/앞으로가기)
		const handlePopstate = (event) => {
			if (event.state?.scrollY) {
				setTimeout(() => {
					window.scrollTo(0, event.state.scrollY);
				}, 100);
			}
		};

		window.addEventListener("scroll", saveScrollToHistory);
		window.addEventListener("beforeunload", saveScrollToHistory);
		window.addEventListener("popstate", handlePopstate);

		return () => {
			saveScrollToHistory();
			clearTimeout(scrollTimeout);
			window.removeEventListener("scroll", saveScrollToHistory);
			window.removeEventListener("beforeunload", saveScrollToHistory);
			window.removeEventListener("popstate", handlePopstate);
		};
	}, [issueId, location.pathname, location.search]);

	// 이슈 데이터 로드 완료 후 추가 스크롤 복원
	useEffect(() => {
		if (issue) {
			const state = window.history.state;
			if (state?.scrollY && typeof state.scrollY === "number") {
				setTimeout(() => {
					window.scrollTo(0, state.scrollY);
				}, 100);
			}
		}
	}, [issue]);

	// 로그인 프롬프트 표시 여부 결정
	useEffect(() => {
		// 팝업 비활성화 (개발 중)
		setShowLoginPrompt(false);
	}, [isLogin]);

	// 콜백 페이지에서 돌아온 경우 처리
	useEffect(() => {
		const returnUrl = sessionStorage.getItem("magazine_return_url");
		if (returnUrl && isLogin) {
			sessionStorage.removeItem("magazine_return_url");
			// 이미 해당 페이지에 있으므로 추가 리다이렉트 불필요
		}

		// 카페 등록 플로우에서 돌아온 경우 처리
		const cafeRegistrationReturn = sessionStorage.getItem(
			"cafe_registration_return"
		);
		if (cafeRegistrationReturn === "true" && isLogin) {
			sessionStorage.removeItem("cafe_registration_return");
			sessionStorage.removeItem("magazine_issue_id");
			// 카페 등록 모달을 자동으로 열기
			setShowCafeRegistrationModal(true);
		}
	}, [isLogin]);

	// issue subscribe (supports both 'issues' and legacy 'issue')
	const selectedRefTypeRef = useRef(null);
	useEffect(() => {
		if (!issueId) return;
		selectedRefTypeRef.current = null;
		setActiveRefType(null);
		setIssue(null);
		setItems([]);

		// 로딩 상태 초기화
		setIssueLoading(true);
		setCollectionsLoading(true);
		setUsersLoading(true);

		const refIssues = dbService
			.collection(MAG_ROOT)
			.doc(MAG_ID)
			.collection("issues")
			.doc(issueId);
		const refIssue = dbService
			.collection(MAG_ROOT)
			.doc(MAG_ID)
			.collection("issue")
			.doc(issueId);

		const unsubA = refIssues.onSnapshot(
			(snap) => {
				if (snap.exists) {
					// Always prefer 'issues' collection
					selectedRefTypeRef.current = "issues";
					setActiveRefType("issues");
					setIssueError(null);
					const issueData = { id: snap.id, ...snap.data() };
					setIssue(issueData);
					setIssueLoading(false);
				} else if (selectedRefTypeRef.current === "issues") {
					setIssue(null);
				}
			},
			(err) => {
				console.error("[MagazineIssue] Issue(issues) subscribe error:", err);
				setIssueError(err?.message || "Failed to load issue");
				setIssueLoading(false);
			}
		);

		const unsubB = refIssue.onSnapshot(
			(snap) => {
				// Only use legacy 'issue' if 'issues' doesn't exist
				if (snap.exists && selectedRefTypeRef.current !== "issues") {
					if (!selectedRefTypeRef.current) {
						selectedRefTypeRef.current = "issue";
						setActiveRefType("issue");
					}
					setIssueError(null);
					setIssue({ id: snap.id, ...snap.data() });
					setIssueLoading(false);
				} else if (snap.exists && selectedRefTypeRef.current === "issue") {
					setIssue({ id: snap.id, ...snap.data() });
					setIssueLoading(false);
				} else if (selectedRefTypeRef.current === "issue") {
					setIssue(null);
				}
			},
			(err) => {
				console.error("[MagazineIssue] Issue(issue) subscribe error:", err);
				if (!selectedRefTypeRef.current) {
					setIssueError(err?.message || "Failed to load issue");
					setIssueLoading(false);
				}
			}
		);

		return () => {
			unsubA && unsubA();
			unsubB && unsubB();
		};
	}, [issueId]);

	// items subscribe according to activeRefType
	useEffect(() => {
		if (!issueId || !activeRefType) return;
		const ref = dbService
			.collection(MAG_ROOT)
			.doc(MAG_ID)
			.collection(activeRefType)
			.doc(issueId)
			.collection("items");
		const unsub = ref.orderBy("order", "asc").onSnapshot(
			(snap) => {
				setItemsError(null);
				const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
				// 데이터 수신 시 바로 셔플하여 상태 저장
				const arr = [...next];
				for (let i = arr.length - 1; i > 0; i--) {
					const j = Math.floor(Math.random() * (i + 1));
					[arr[i], arr[j]] = [arr[j], arr[i]];
				}
				setItems(arr);
			},
			(err) => {
				console.error("[MagazineIssue] Items subscribe error:", err);
				setItemsError(err?.message || "Failed to load items");
				setItems([]);
			}
		);
		return () => unsub && unsub();
	}, [issueId, activeRefType]);

	// load collections and users of items
	useEffect(() => {
		const load = async () => {
			const colIds = Array.from(
				new Set(items.map((it) => it.collectionId).filter(Boolean))
			);
			const missingCols = colIds.filter((id) => !collectionInfoById[id]);
			if (missingCols.length > 0) {
				const snaps = await Promise.all(
					missingCols.map((id) =>
						dbService.collection("collection").doc(id).get()
					)
				);
				const next = { ...collectionInfoById };
				snaps.forEach((snap, idx) => {
					const id = missingCols[idx];
					if (snap.exists) next[id] = { id, ...snap.data() };
				});
				setCollectionInfoById(next);
			}

			const userIds = Array.from(
				new Set(items.map((it) => it.userId).filter(Boolean))
			);
			const missingUsers = userIds.filter((id) => !userInfoById[id]);
			if (missingUsers.length > 0) {
				const snaps = await Promise.all(
					missingUsers.map((id) => dbService.collection("user").doc(id).get())
				);
				const next = { ...userInfoById };
				snaps.forEach((snap, idx) => {
					const id = missingUsers[idx];
					if (snap.exists) next[id] = { id, ...snap.data() };
				});
				setUserInfoById(next);
			}

			// 컬렉션 로딩 완료
			setCollectionsLoading(false);
		};
		if (items.length > 0) {
			load();
		} else if (items.length === 0 && !issueLoading) {
			// 아이템이 없는 경우에도 로딩 완료 처리
			setCollectionsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, issueLoading]);

	// build thread map from collections (avoid unnecessary updates)
	useEffect(() => {
		const map = {};
		Object.values(collectionInfoById).forEach((col) => {
			const list = Array.isArray(col?.threadList) ? col.threadList : [];
			list.forEach((th) => {
				if (th?.id) map[String(th.id)] = { ...th, collectionId: col.id };
			});
		});
		const prev = threadInfoById;
		const prevKeys = Object.keys(prev);
		const nextKeys = Object.keys(map);
		let equal = prevKeys.length === nextKeys.length;
		if (equal) {
			for (let i = 0; i < nextKeys.length; i++) {
				const k = nextKeys[i];
				const a = prev[k];
				const b = map[k];
				if (!a || !b || a.id !== b.id || a.collectionId !== b.collectionId) {
					equal = false;
					break;
				}
			}
		}
		if (!equal) setThreadInfoById(map);
	}, [collectionInfoById, threadInfoById]);

	const coverStoryThreads = useMemo(() => {
		const ids = Array.isArray(issue?.coverStoryList)
			? issue.coverStoryList.map(String)
			: [];
		const threads = ids.map((id) => threadInfoById[id]).filter(Boolean);
		if (threads.length > 0) return threads;
		// 플레이스홀더 6개 (빈 UI 방지)
		return Array.from({ length: 6 }, (_, i) => ({
			id: `cover-ph-${i + 1}`,
			collectionId: "placeholder",
			text: "Placeholder",
		}));
	}, [issue, threadInfoById]);

	// Cover Story를 데이터 단계에서 1회 셔플
	useEffect(() => {
		if (!coverStoryThreads || coverStoryThreads.length === 0) {
			setShuffledCoverStoryThreads([]);
			return;
		}
		const arr = [...coverStoryThreads];
		for (let i = arr.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[arr[i], arr[j]] = [arr[j], arr[i]];
		}
		setShuffledCoverStoryThreads(arr);
	}, [coverStoryThreads]);

	// 섹션 내 글 리스트를 데이터 수신 직후 1회 셔플하여 상태로 보관
	useEffect(() => {
		if (!Array.isArray(issue?.sectionList) || issue.sectionList.length === 0) {
			setShuffledSections([]);
			return;
		}
		const list = issue.sectionList.map((sec) => {
			const ids = Array.isArray(sec.threadIdList) ? [...sec.threadIdList] : [];
			for (let i = ids.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[ids[i], ids[j]] = [ids[j], ids[i]];
			}
			return { ...sec, threadIdList: ids };
		});
		setShuffledSections(list);
	}, [issue?.sectionList]);

	// 섹션을 2개 단위로 청크화 (사전 셔플 적용된 섹션 사용)
	const sectionChunks = useMemo(() => {
		const list = shuffledSections;
		const chunks = [];
		for (let i = 0; i < list.length; i += 2) {
			chunks.push({ start: i, list: list.slice(i, i + 2) });
		}
		return chunks;
	}, [shuffledSections]);



	// 참가자 ID 순서를 1회만 셔플하여 고정
	useEffect(() => {
		const ids = Array.isArray(issue?.userIdList)
			? issue.userIdList.map(String)
			: [];
		if (ids.length === 0) return;
		if (participantsIdsRef.current.length > 0) return; // 이미 생성됨
		const shuffled = [...ids];
		for (let i = shuffled.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
		}
		participantsIdsRef.current = shuffled;
	}, [issue?.userIdList]);

	// threadsPeople 조인 제거 (원래 참가자 표시 유지)

	// 모든 참가자 데이터가 매핑 가능할 때까지 준비 상태 대기 (렌더 게이트)
	useEffect(() => {
		const ids = participantsIdsRef.current;
		if (!ids.length) return;
		const allResolvable = ids.every((uid) => {
			const user = userInfoById[uid];
			const firstCol = items.find((it) => String(it.userId) === String(uid));
			const colDoc = firstCol
				? collectionInfoById[firstCol.collectionId]
				: null;
			const hasFallback = !!(
				colDoc?.profileImage ||
				colDoc?.profile?.threads_profile_picture_url ||
				colDoc?.username ||
				colDoc?.profile?.username
			);
			return !!user || hasFallback;
		});
		setParticipantsReady(allResolvable);
	}, [userInfoById, items, collectionInfoById]);

	// 준비 완료 후에만 고정 순서에 데이터 주입 (원래 로직 유지)
	useEffect(() => {
		if (!participantsReady) return;
		const ids = participantsIdsRef.current;
		const resolved = ids.map((uid) => {
			const userDoc = userInfoById[uid] || { id: uid };
			const firstCol = items.find((it) => String(it.userId) === String(uid));
			const colDoc = firstCol
				? collectionInfoById[firstCol.collectionId]
				: null;
			const fallbackAvatar =
				colDoc?.profileImage ||
				colDoc?.profile?.threads_profile_picture_url ||
				null;
			const username =
				userDoc?.profile?.username ||
				userDoc?.username ||
				colDoc?.profile?.username ||
				colDoc?.username ||
				"";
			const name =
				userDoc?.profile?.name ||
				userDoc?.name ||
				colDoc?.profile?.name ||
				colDoc?.name ||
				username;
			return { ...userDoc, __fallbackAvatar: fallbackAvatar, username, name };
		});
		setParticipants(resolved);
	}, [participantsReady, userInfoById, items, collectionInfoById]);

	// participants 데이터가 완전히 준비되고 셔플 완료되면 users 로딩 상태 해제
	useEffect(() => {
		if (!issueLoading && !collectionsLoading && usersLoading) {
			if (participantsReady) {
				setTimeout(() => setUsersLoading(false), 50);
			} else if (!issue?.userIdList?.length || issue.userIdList.length === 0) {
				setUsersLoading(false);
			}
		}
	}, [
		participantsReady,
		issueLoading,
		collectionsLoading,
		usersLoading,
		issue?.userIdList,
	]);

	// 플레이스홀더 데이터 (섹션에서 직접 생성하므로 여기서는 제거)

	// 섹션 비디오 지연 캐싱 (사용자가 스크롤할 때 시작)
	const observerSetupKey = useMemo(
		() => `${issueId || ""}:${issue?.sectionList?.length || 0}`,
		[issueId, issue?.sectionList?.length]
	);
	const observerKeyRef = useRef(null);
	useEffect(() => {
		if (!issue?.sectionList || sectionVideoCaches.length === 0) return;
		if (observerKeyRef.current === observerSetupKey) return; // 이미 설정됨
		observerKeyRef.current = observerSetupKey;

		if (process.env.NODE_ENV === "development") {
			console.log(
				`📹 Setting up video caching for ${sectionVideoCaches.length} sections`
			);
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const sectionIndex = parseInt(entry.target.dataset.sectionIndex);
						const cache = sectionVideoCaches[sectionIndex];

						if (cache && !cache.isCached && !cache.isLoading) {
							if (process.env.NODE_ENV === "development") {
								console.log(
									`🎬 Starting cache for section ${sectionIndex + 1} video`
								);
							}
							cache.cacheVideo();
						} else if (cache) {
							if (process.env.NODE_ENV === "development") {
								console.log(
									`📼 Section ${sectionIndex + 1} video cache status:`,
									{
										isCached: cache.isCached,
										isLoading: cache.isLoading,
										progress: cache.progress,
									}
								);
							}
						}
					}
				});
			},
			{
				rootMargin: "200px 0px", // 섹션이 200px 전에 미리 캐싱 시작
				threshold: 0.1, // 10% 보일 때 트리거
			}
		);

		// 약간의 지연 후 섹션 요소들을 찾아서 관찰 시작
		const setupObserver = () => {
			const sectionElements = document.querySelectorAll("[data-section-index]");
			console.log(
				`🔍 Found ${sectionElements.length} section elements to observe`
			);

			sectionElements.forEach((el, index) => {
				console.log(
					`👀 Observing section ${index}: data-section-index=${el.dataset.sectionIndex}`
				);
				observer.observe(el);
			});
		};

		// DOM이 렌더링된 후 observer 설정
		const timer = setTimeout(setupObserver, 100);

		return () => {
			clearTimeout(timer);
			observer.disconnect();
		};
	}, [observerSetupKey, issue?.sectionList, sectionVideoCaches]);

	// 비디오 캐시 정보를 자식 컴포넌트에 전달하기 위한 객체
	const videoCacheInfo = useMemo(
		() => ({
			coverVideo: {
				url: coverVideoUrl,
				cache: coverVideoCache,
			},
			sectionVideos: sectionVideoUrls.map((url, index) => ({
				url,
				cache: sectionVideoCaches[index] || null,
			})),
		}),
		[coverVideoUrl, coverVideoCache, sectionVideoUrls, sectionVideoCaches]
	);

	// 모든 컬렉션 그리드에서 마지막 줄 남은 칸 수에 따라 NextIssueCard 크기 동적 계산
	const nextIssueGridSize = useMemo(() => {
		const perRowXs = 2; // xs: size=6 → 2개/행
		const perRowMd = 4; // md+: size=3 → 4개/행
		const len = items.length;
		const remainderXs = len % perRowXs;
		const remainderMd = len % perRowMd;
		const emptyXs = remainderXs === 0 ? perRowXs : perRowXs - remainderXs; // 1→6, 2(=0)→12
		const emptyMd = remainderMd === 0 ? perRowMd : perRowMd - remainderMd; // 1→3, 2→6, 3→9, 4(=0)→12
		return {
			xs: emptyXs * 6,
			sm: emptyXs * 6,
			md: emptyMd * 3,
			lg: emptyMd * 3,
		};
	}, [items.length]);

	// 개발 환경에서 캐시 상태 디버깅
	useEffect(() => {
		if (process.env.NODE_ENV === "development") {
			const logCacheInfo = async () => {
				console.group("📹 Video Cache Status");

				// 커버 비디오 상태
				console.log("🎬 Cover Video:", {
					url: coverVideoUrl,
					isCached: coverVideoCache.isCached,
					isLoading: coverVideoCache.isLoading,
					progress: coverVideoCache.progress,
					error: coverVideoCache.error,
				});

				// 섹션 비디오 상태들
				sectionVideoCaches.forEach((cache, index) => {
					console.log(`📼 Section ${index + 1} Video:`, {
						url: sectionVideoUrls[index],
						isCached: cache.isCached,
						isLoading: cache.isLoading,
						progress: cache.progress,
						error: cache.error,
					});
				});

				// 전체 캐시 정보
				const cacheInfo = await getCacheInfo();
				if (cacheInfo) {
					console.log(
						`📊 Total cached: ${cacheInfo.entryCount} videos (${cacheInfo.totalSizeMB}MB)`
					);
					console.log("🗃️ Cache entries:", cacheInfo.entries);
				}

				console.groupEnd();
			};

			const timer = setTimeout(logCacheInfo, 3000); // 3초 후 로그 출력
			return () => clearTimeout(timer);
		}
	}, [
		issue,
		coverVideoCache,
		sectionVideoCaches,
		coverVideoUrl,
		sectionVideoUrls,
	]);

	// 모든 컬렉션은 스냅샷 수신 시점에 이미 셔플됨

	// 헤더를 페이지 최상단에 항상 배치하고, 본문은 조건부 렌더링

	return (
		<Stack
			spacing={isMobile ? 4 : 8}
			sx={{
				width: "100%",
				maxWidth: "xl",
				p: isMobile ? 1 : 2,
				minWidth: 0,
				alignSelf: "center",
				alignItems: "center",
			}}
		>
			{/* 0. Page Title */}
			<MagazineIssueHeader
				issue={issue}
				isMobile={isMobile}
				isLoading={issueLoading}
			/>

			{/* 1. Top section (5/7 grid inside component, participants below in one row) */}
			<Stack spacing={0} sx={{ minWidth: 0, overflowX: "hidden" }}>
				<MagazineSectionContainer>
					<MagazineTopSection
						title={issue?.title || issue?.id || ""}
						description={issue?.description || ""}
						isMobile={isMobile}
						videoCacheInfo={videoCacheInfo.coverVideo}
						isLoading={issueLoading}
					/>
				</MagazineSectionContainer>
				<Box
					sx={{
						width: "100%",
						borderBottom: "1px solid",
						borderColor: "primary.main",
						minWidth: 0,
						overflowX: "hidden",
					}}
				>
					<MagazineParticipantsStrip
						users={participantsReady ? participants : []}
						isMobile={isMobile}
					/>
				</Box>
			</Stack>
			{/* 2. Cover Story */}
			<MagazineSectionContainer
				title="커버 스토리"
				titleAlign="left"
				sx={{ overflow: "visible", minWidth: 0 }}
			>
				<Stack spacing={1} sx={{ minWidth: 0 }}>
					<MagazineCoverCarousel
						threads={shuffledCoverStoryThreads}
						userInfoById={userInfoById}
						collectionInfoById={collectionInfoById}
						isMobile={isMobile}
						isLoading={issueLoading || collectionsLoading}
					/>
				</Stack>
			</MagazineSectionContainer>

			{/* items/issue load error notice (non-blocking) */}
			{(itemsError || issueError) && (
				<Typography variant="caption" color="error" sx={{ mb: 2 }}>
					아이템/이슈를 불러오지 못했습니다: {itemsError || issueError}
				</Typography>
			)}
			{/* 4. Sections - 2개 단위 청크 + 배너/참여자 쇼케이스 교차 배치 */}
			{sectionChunks.map((chunk, idx) => (
				<React.Fragment key={`sec-chunk-${idx}`}>
					<MagazineSectionContainer
						title={idx === 0 ? "에디터 픽" : undefined}
						sx={{ minWidth: 0, overflowX: "hidden" }}
					>
						<MagazineSectionsBlock
							sections={chunk.list}
							threadInfoById={threadInfoById}
							userInfoById={userInfoById}
							collectionInfoById={collectionInfoById}
							isMobile={isMobile}
							issueId={issueId}
							videoCacheInfo={videoCacheInfo.sectionVideos.slice(
								chunk.start,
								chunk.start + chunk.list.length
							)}
							isLoading={issueLoading || collectionsLoading}
							sectionIndexOffset={chunk.start}
							addTopBorder={idx > 0}
						/>
					</MagazineSectionContainer>

					{idx % 2 === 0 ? (
						<MagazineFooterBanner isMobile={isMobile} />
					) : (
						<ParticipantsShowcaseSection
							participants={participantsReady ? participants : []}
							isMobile={isMobile}
						/>
					)}
				</React.Fragment>
			))}

			{/* 4.5. Threads Cafe Map (2025-09 한정) */}
			{issueId === "2025-09" && (
				<Box
					component="section"
					sx={{
						width: "100%",
						maxWidth: "100%",
						minWidth: 0,
						display: "block",
						overflow: "hidden",
						boxSizing: "border-box",
						contain: "layout style",
						alignSelf: "stretch",
						flex: "1 1 auto",
					}}
				>
					<SectionTitle title="스레드 카페지도" />
					<ThreadsCafeMap
						fullScreen={false}
						containerSx={{
							width: "100%",
							height: "auto",
							minHeight: { xs: 360, sm: 400, md: 480 },
						}}
						initialParticipants={participants}
						initialCollections={collectionsForIssue}
						initialShowCafeRegistrationModal={showCafeRegistrationModal}
						onToggleCafeRegistrationModal={setShowCafeRegistrationModal}
					/>
				</Box>
			)}

			{/* 5. All collections */}
			<MagazineSectionContainer
				title="모든 컬렉션"
				titleAlign="left"
				sx={{ minWidth: 0, overflowX: "hidden" }}
			>
				<Box sx={{ minWidth: 0, overflowX: "hidden" }}>
					<LineGrid
						gap={0}
						borderColor="primary.main"
						gridColumns={isMobile ? 2 : 4}
					>
						{items.map((it) => {
							const col = collectionInfoById[it.collectionId];
							const user = userInfoById[it.userId];
							console.log(
								"Item:",
								it.id,
								"Collection:",
								col?.title || "missing"
							);
							if (!col) return null;
							return (
								<Grid key={it.id} size={{ xs: 6, sm: 6, md: 3, lg: 3 }}>
									<MagazineCollectionCard
										collection={col}
										user={user}
										isMobile={isMobile}
										categoryLabels={(() => {
											const direct = Array.isArray(col?.categoryLabels)
												? col.categoryLabels
												: Array.isArray(col?.tags)
													? col.tags
													: Array.isArray(col?.categories)
														? col.categories.map(
															(c) => c?.label || c?.name || c?.key || String(c)
														)
														: [];
											return direct.filter(Boolean);
										})()}
									/>
								</Grid>
							);
						})}
						{/* 다음 호 안내 카드 (남은 칸을 채우도록 동적 크기) */}
						{!(
							nextIssueGridSize?.md === 12 || nextIssueGridSize?.lg === 12
						) && (
								<Grid size={nextIssueGridSize}>
									<MagazineNextIssueCard
										nextIssue={(() => {
											const match = issueId?.match(/(\d{4})-(\d{2})/);
											if (!match) return "다음호";
											const [, year, month] = match;
											if (!year || !month) return "다음호";
											const nextMonth = parseInt(month) + 1;
											if (nextMonth > 12) {
												return `${parseInt(year) + 1}년 1월호`;
											}
											return `${nextMonth}월호`;
										})()}
										nextTitle={issue?.nextTitle}
										isMobile={isMobile}
									/>
								</Grid>
							)}
					</LineGrid>
				</Box>
			</MagazineSectionContainer>

			{/* All collections 컨테이너 바깥: 4칸 배너 대체 */}
			{(nextIssueGridSize?.md === 12 || nextIssueGridSize?.lg === 12) && (
				<Box sx={{ width: "100%", mt: 2 }}>
					<MagazineFooterBannerCard
						isMobile={isMobile}
						nextIssue={(() => {
							const match = issueId?.match(/(\d{4})-(\d{2})/);
							if (!match) return "다음호";
							const [, year, month] = match;
							if (!year || !month) return "다음호";
							const nextMonth = parseInt(month) + 1;
							if (nextMonth > 12) {
								return `${parseInt(year) + 1}년 1월호`;
							}
							return `${nextMonth}월호`;
						})()}
						nextTitle={issue?.nextTitle}
					/>
				</Box>
			)}

			{/* 로그인 프롬프트 */}
			<MagazineLoginPrompt
				open={showLoginPrompt}
				onClose={() => setShowLoginPrompt(false)}
				isMobile={isMobile}
			/>

			{/* 컬렉션 팝업 모달 */}
			<CollectionPopupModal
				open={!!selectedCollectionId}
				onClose={() => setSelectedCollectionId(null)}
				collectionId={selectedCollectionId}
				isMobile={isMobile}
			/>
		</Stack>
	);
};

export default MagazineIssue;
